import { createFileRoute, Link, Outlet, redirect, useLocation } from '@tanstack/react-router'
import { useState } from 'react'
import { FEATURES, type FeatureKey } from '@academy/config'
import { hasSession } from '../lib/auth-client.js'
import { useAuth } from '../lib/auth.js'
import { fetchMe, useMe } from '../lib/me.js'
import { useFeatures, type FeatureMap } from '../lib/features.js'
import { Sidebar, type NavSection } from '../components/Sidebar.js'
import { Logo } from '../components/Logo.js'
import { Icon } from '../components/Icon.js'
import { AcademySuspended } from '../components/AcademySuspended.js'

const BLOCKED_STATUSES = new Set(['suspended', 'cancelled'])

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ location }) => {
    if (!(await hasSession())) {
      throw redirect({ to: '/login' })
    }
    // Funnel brand-new self-signups (no academy, no children) to academy
    // onboarding from anywhere in the app — but don't loop on the form itself.
    if (location.pathname !== '/onboarding/register') {
      const me = await fetchMe()
      if (me?.needsOnboarding) {
        throw redirect({ to: '/onboarding/register' })
      }
    }
  },
  component: AppShell,
})

function AppShell() {
  const { user, signOut } = useAuth()
  const location = useLocation()
  // Role + academy come from /api/v1/me — Better Auth issues opaque session
  // tokens, so there are no client-readable JWT claims to decode.
  const { me } = useMe()
  const role = me?.role ?? 'pending'
  const academyId = me?.academyId

  // Only academy-scoped roles have a resolved feature map. Super admins and
  // not-yet-onboarded admins have no academy, so we skip the fetch for them.
  const featuresEnabled =
    (role === 'academy_admin' || role === 'teacher') && !!academyId
  const { features } = useFeatures(featuresEnabled)

  // signOut() clears the Better Auth session + local bearer token. We await it,
  // then do a hard window navigation so the next page load re-runs the
  // `beforeLoad` gate against the cleared session (deterministic vs a SPA
  // navigate that could race the auth state update).
  const handleSignOut = async () => {
    await signOut()
    window.location.assign('/login')
  }

  // A suspended/cancelled academy is locked out (the API enforces this with 403
  // ACADEMY_SUSPENDED). Show a full-page lockout instead of the dashboard, but
  // leave /billing reachable so the owner can pay to reactivate. Placed after all
  // hooks so the hook call order stays stable across renders. Pass sign-out so a
  // locked-out user isn't trapped with no way to leave the session.
  const academyBlocked = !!me?.academyStatus && BLOCKED_STATUSES.has(me.academyStatus)
  if (academyBlocked && !location.pathname.startsWith('/billing')) {
    return <AcademySuspended status={me!.academyStatus!} onSignOut={handleSignOut} />
  }

  const sections = navSectionsForRole(role, !!academyId, features)

  return (
    <div className="min-h-screen flex bg-surface-muted">
      <Sidebar
        sections={sections}
        footer={
          <div className="space-y-2.5">
            <div>
              <p className="text-sm text-fg truncate font-medium" title={user?.email}>
                {user?.email}
              </p>
              <p className="text-xs text-fg-subtle capitalize">
                {role.replace('_', ' ')}
              </p>
            </div>
            <button
              onClick={handleSignOut}
              className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
            >
              <Icon name="logOut" size={13} />
              Sign out
            </button>
          </div>
        }
      />
      <div className="flex-1 flex flex-col min-w-0">
        <MobileBar
          role={role}
          signOut={handleSignOut}
          email={user?.email}
          sections={sections}
        />
        <main className="flex-1 px-6 md:px-10 py-8 max-w-[1280px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

function MobileBar({
  role,
  signOut,
  email,
  sections,
}: {
  role: string
  signOut: () => Promise<void>
  email?: string
  sections: NavSection[]
}) {
  // Controlled (not native <details>) so the menu closes on in-SPA navigation —
  // otherwise it would stay open over the page the user just navigated to.
  const [open, setOpen] = useState(false)

  return (
    <div className="md:hidden flex items-center justify-between border-b border-border px-4 py-3 bg-surface relative">
      <Logo />
      <button
        onClick={() => setOpen((o) => !o)}
        className="text-sm text-fg-muted"
        aria-expanded={open}
        aria-label="Menu"
      >
        Menu ▾
      </button>
      {open ? (
        <>
          {/* Backdrop: tap outside closes the menu. */}
          <button
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div className="absolute right-4 top-full mt-2 w-64 rounded-md border border-border bg-surface shadow-md z-50 overflow-hidden">
            <nav className="max-h-[70vh] overflow-y-auto px-2 py-2 space-y-3">
              {sections.map((group, gi) => (
                <div key={gi} className="space-y-0.5">
                  {group.label ? (
                    <p className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-fg-faint">
                      {group.label}
                    </p>
                  ) : null}
                  {group.items.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setOpen(false)}
                      activeProps={{ className: 'is-active' }}
                      className="group relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-fg-muted hover:bg-surface-subtle hover:text-fg transition-colors [&.is-active]:bg-[var(--color-primary-tint)] [&.is-active]:text-fg [&.is-active]:font-medium"
                    >
                      {item.icon ? (
                        <Icon
                          name={item.icon}
                          size={16}
                          className="text-fg-subtle group-[.is-active]:text-[var(--color-primary)]"
                        />
                      ) : null}
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
              ))}
            </nav>
            <div className="px-4 py-3 border-t border-border">
              <p className="text-sm text-fg truncate">{email}</p>
              <p className="text-xs text-fg-subtle capitalize">
                {role.replace('_', ' ')}
              </p>
            </div>
            <button
              onClick={signOut}
              className="flex w-full items-center gap-1.5 border-t border-border px-4 py-3 text-sm text-fg hover:bg-surface-muted"
            >
              <Icon name="logOut" size={14} />
              Sign out
            </button>
          </div>
        </>
      ) : null}
    </div>
  )
}

// A nav item with an optional feature gate. When `feature` is set, the item is
// only shown if that flag is enabled for the academy. Items with no gate (e.g.
// Students, Classes, Settlements) always show for the role.
type GatedSection = {
  label?: string
  items: Array<NavSection['items'][number] & { feature?: FeatureKey }>
}

function navSectionsForRole(
  role: string,
  hasAcademy: boolean,
  features: FeatureMap,
): NavSection[] {
  const sections = rawSectionsForRole(role, hasAcademy)
  // Drop feature-gated items that are off, then drop any section left empty.
  return sections
    .map((section) => ({
      label: section.label,
      items: section.items
        .filter((item) => !item.feature || features[item.feature])
        .map(({ feature: _feature, ...item }) => item),
    }))
    .filter((section) => section.items.length > 0)
}

function rawSectionsForRole(role: string, hasAcademy: boolean): GatedSection[] {
  if (role === 'super_admin') {
    return [
      {
        label: 'Platform',
        items: [
          { to: '/super/academies', label: 'Academies', icon: 'building' },
          { to: '/super/plans', label: 'Plans', icon: 'listChecks' },
          { to: '/settlements', label: 'Settlements', icon: 'wallet' },
          { to: '/super/audit-trail', label: 'Activity', icon: 'fileText' },
          { to: '/super/health', label: 'System health', icon: 'alert' },
        ],
      },
    ]
  }

  if (role === 'academy_admin' && hasAcademy) {
    return [
      {
        items: [{ to: '/dashboard', label: 'Overview', icon: 'home' }],
      },
      {
        label: 'People',
        items: [
          { to: '/students', label: 'Students', icon: 'users' },
          { to: '/classes', label: 'Classes', icon: 'graduationCap' },
          {
            to: '/teachers',
            label: 'Teachers',
            icon: 'users',
            feature: FEATURES.TEACHER_ACCOUNTS,
          },
        ],
      },
      {
        label: 'Academics',
        items: [
          {
            to: '/tests',
            label: 'Tests',
            icon: 'clipboardCheck',
            feature: FEATURES.PERFORMANCE,
          },
          {
            to: '/exams',
            label: 'Exams',
            icon: 'calendar',
            feature: FEATURES.EXAM_BOARDS,
          },
        ],
      },
      {
        label: 'Money',
        items: [
          { to: '/fees', label: 'Fees', icon: 'wallet', feature: FEATURES.FEE_COLLECTION },
          { to: '/settlements', label: 'Settlements', icon: 'wallet' },
          { to: '/billing', label: 'Subscription', icon: 'creditCard' },
        ],
      },
      {
        label: 'Communicate',
        items: [
          {
            to: '/communicate',
            label: 'Broadcasts',
            icon: 'inbox',
            feature: FEATURES.PARENT_COMMUNICATION,
          },
        ],
      },
      {
        label: 'Setup',
        items: [
          { to: '/settings/payments', label: 'Payments', icon: 'creditCard' },
          {
            to: '/settings/branding',
            label: 'Branding',
            icon: 'settings',
            feature: FEATURES.WHITE_LABEL,
          },
          { to: '/audit-trail', label: 'Activity', icon: 'fileText' },
        ],
      },
    ]
  }

  if (role === 'academy_admin' && !hasAcademy) {
    return [
      {
        items: [
          {
            to: '/onboarding/register',
            label: 'Set up academy',
            icon: 'building',
          },
        ],
      },
    ]
  }

  if (role === 'teacher') {
    return [
      {
        items: [
          { to: '/my-classes', label: 'My classes', icon: 'bookOpen' },
          {
            to: '/tests',
            label: 'Tests',
            icon: 'clipboardCheck',
            feature: FEATURES.PERFORMANCE,
          },
          {
            to: '/exam-calendar',
            label: 'Exam calendar',
            icon: 'calendar',
            feature: FEATURES.EXAM_BOARDS,
          },
        ],
      },
    ]
  }

  if (role === 'parent') {
    return [
      {
        items: [
          { to: '/children', label: 'My children', icon: 'bookOpen' },
          { to: '/my-fees', label: 'Fees', icon: 'wallet' },
        ],
      },
    ]
  }

  return [{ items: [{ to: '/dashboard', label: 'Overview', icon: 'home' }] }]
}
